/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Operations › Fleet health (docs/CONTRACT-fleet-governance.md section 12.2; owner, D1): one page, four views in the page
 * header, each its own `?view=` (overview, pcs, versions, problems), the way Support has its views. Settings opens the
 * settings drawer (editable for IT admins, read-only for auditors); `?pc=<deviceId>` opens the PC drawer over any view.
 *
 * `FleetGate` is what App.tsx renders: the probe's `checking` and `hidden` states as plain pages, `shown` as the page.
 */
import { useState } from 'react';
import { Settings } from 'lucide-react';
import { Button, EmptyState, type PageTab } from '../../ui';
import { useQueryParam } from '../pulse/common';
import { FleetOverview } from './Overview';
import { FleetPcs } from './Pcs';
import { FleetVersions } from './Versions';
import { FleetProblems } from './Problems';
import { PcDrawer } from './PcDrawer';
import { FleetSettingsDrawer } from './FleetSettings';
import { PlainPage, type FleetFrame } from './parts';

type View = 'overview' | 'pcs' | 'versions' | 'problems';
const VIEWS: readonly View[] = ['overview', 'pcs', 'versions', 'problems'];
const VIEW_LABELS: Record<View, string> = { overview: 'Overview', pcs: 'PCs', versions: 'Versions', problems: 'Problems' };
const isView = (value: string | null): value is View => (VIEWS as readonly string[]).includes(value ?? '');

export function FleetGate({ state, canEdit }: { state: 'checking' | 'shown' | 'hidden'; canEdit: boolean }) {
  if (state === 'checking') {
    return <PlainPage><EmptyState title="Checking whether Fleet health is on">The Fleet health views appear here once the service answers.</EmptyState></PlainPage>;
  }
  if (state === 'hidden') {
    return (
      <PlainPage>
        <EmptyState title="Fleet health is not switched on">
          Fleet health shows every PC with Gyro, its version, when it was last seen and what is failing on it. It appears here once the fleet flag is turned on under Feature flags.
        </EmptyState>
      </PlainPage>
    );
  }
  return <FleetPage canEdit={canEdit} />;
}

export function FleetPage({ canEdit }: { canEdit: boolean }) {
  const [asked] = useQueryParam('view');
  const [pc, setPc] = useQueryParam('pc');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const view: View = isView(asked) ? asked : 'overview';
  const tabs: PageTab[] = VIEWS.map((id) => ({ id, label: VIEW_LABELS[id], href: `/fleet?view=${id}` }));
  const frame: FleetFrame = {
    tabs,
    activeTab: view,
    actions: <Button size="sm" icon={Settings} onClick={() => setSettingsOpen(true)}>Settings</Button>,
    openPc: (deviceId) => setPc(deviceId, { replace: false }),
  };

  let content;
  switch (view) {
    case 'pcs': content = <FleetPcs frame={frame} />; break;
    case 'versions': content = <FleetVersions frame={frame} />; break;
    case 'problems': content = <FleetProblems frame={frame} />; break;
    default: content = <FleetOverview frame={frame} />;
  }

  return (
    <>
      {content}
      <PcDrawer deviceId={pc} onClose={() => setPc(null)} />
      <FleetSettingsDrawer open={settingsOpen} canEdit={canEdit} onClose={() => setSettingsOpen(false)} />
    </>
  );
}
